import { OBJECTS } from '../data/objects.js';

/**
 * What mass decides.
 *
 * Every question the game used to answer with a per-item tuning table — how
 * many fit in your arms, how much they slow you down, how many fit on a cart,
 * whether you need both hands, how far you can throw one — is answered here,
 * from one number.
 *
 * The budgets are chosen so the three banquet items land exactly where they
 * were hand-tuned: a 35kg round gives two in the arms at full burden, a 3.8kg
 * chair gives twelve at 0.65, a 2kg linen gives twelve at 0.34. That was not a
 * coincidence, it was the point — a general rule that changes the numbers it
 * replaces is a general rule that broke the game to prove itself.
 */

/** Kilograms a pair of arms will carry before they are full. */
export const HAND_BUDGET = 70;

/** However light a thing is, twelve of them is an armful. */
export const HAND_MAX = 12;

/** A cart takes twice the weight and more of the awkward light stuff. */
export const CART_BUDGET = 140;
export const CART_MAX = 16;

/**
 * Past this, it takes both hands.
 *
 * Both hands means no climbing, a weaker throw, and no opening a door while you
 * hold it. It is not a separate carry mode, it is a consequence of the mass.
 */
export const TWO_HAND_MASS = 12;

/** Above this, a thrown object hurts somebody. Below it, it just annoys them. */
export const HURT_MASS = 0.5;

export function definition(kind) {
  return OBJECTS[kind] ?? null;
}

export function massOf(kind) {
  return OBJECTS[kind]?.mass ?? 1;
}

/** How many fit, given a budget and a cap. */
export function stackLimit(kind, { budget = HAND_BUDGET, cap = HAND_MAX } = {}) {
  const mass = massOf(kind);
  if (!mass) return cap;
  const override = OBJECTS[kind]?.stack;
  if (override) return Math.max(1, Math.min(override, cap));
  return Math.max(1, Math.min(Math.floor(budget / mass), cap));
}

/** 0..1 movement penalty for holding `count` of something. */
export function burdenOf(kind, count, { budget = HAND_BUDGET } = {}) {
  if (!count) return 0;
  return Math.min(1, (massOf(kind) * count) / budget);
}

export function twoHanded(kind) {
  return massOf(kind) >= TWO_HAND_MASS;
}

/**
 * How a thrown object flies.
 *
 * A light thing goes far and flat, a heavy thing is a heave. Power is the
 * fraction of a full wind-up, 0..1. Both curves are deliberately gentle: this
 * is a hotel, not a trebuchet, and the interesting part is whether anybody saw
 * it rather than how far it went.
 */
export function throwArc(kind, power = 1) {
  const mass = massOf(kind);
  const heavy = twoHanded(kind);
  // 12 m/s for a napkin, about 4 for a fire extinguisher.
  const speed = (heavy ? 5.5 : 12) / (1 + mass / (heavy ? 30 : 6));
  return {
    speed: speed * (0.35 + 0.65 * power),
    /** Radians above the horizontal. Heavier things are lobbed. */
    loft: heavy ? 0.42 : 0.26,
    twoHanded: heavy,
  };
}

/** Kinetic energy at impact, which is what everything downstream marks on. */
export function impactEnergy(kind, speed) {
  return 0.5 * massOf(kind) * speed * speed;
}
